import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from '@tanstack/react-router';
import { QRCodeCanvas } from 'qrcode.react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CheckboxField,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Label,
  PageHeader,
  SelectField,
  Skeleton,
  TextField,
  toast,
} from '@eon/ui';
import { resolveApiError, useTranslation } from '@eon/i18n';
import { hooks } from '@eon/api-client';
import type { Device } from '@eon/types';
import { formatDate, formatRelative } from '@eon/utils';
import { fromSelectValue, SELECT_NONE } from '@/lib/select-none';
import { IdTagPicker } from '@/components/IdTagPicker';
import { ConnectorStatusBadge, DeviceStatusBadge } from '@/components/status/StatusBadges';
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  KeyRound,
  Pencil,
  PlugZap,
  Radio,
  Rss,
  RotateCw,
  Terminal,
  Trash2,
  Wifi,
  WifiOff,
  XCircle,
  Zap,
} from 'lucide-react';

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-muted-foreground text-xs uppercase tracking-wider">{label}</span>
      <span className="text-sm font-medium">{value || <span className="text-muted-foreground">—</span>}</span>
    </div>
  );
}

function ConnectionBadge({ device }: { device: Device }) {
  const { t } = useTranslation();
  const offline = !hooks.isDeviceOnline(device);
  return (
    <Badge variant={offline ? 'destructive' : 'success'} className="gap-1.5">
      {offline ? <WifiOff className="h-3 w-3" /> : <Wifi className="h-3 w-3" />}
      {offline ? t('common.offline') : t('common.online')}
    </Badge>
  );
}

function RegisteredBadge({ device }: { device: Device }) {
  const { t } = useTranslation();
  return device.registered ? (
    <Badge variant="success" className="gap-1.5">
      <CheckCircle2 className="h-3 w-3" /> {t('device.registered')}
    </Badge>
  ) : (
    <Badge variant="warning" className="gap-1.5">
      <XCircle className="h-3 w-3" /> {t('device.pending')}
    </Badge>
  );
}

function ConnectorCard({ device, c, onAction }: {
  device: Device;
  c: NonNullable<Device['connectors']>[number];
  onAction: (kind: 'start' | 'stop' | 'reset', connectorId: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{t('device.connectorTitle', { id: c.id })}</CardTitle>
          <ConnectorStatusBadge id={c.id} status={c.status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <InfoRow label="Tip" value={c.type} />
          <InfoRow label="Format" value={c.format} />
          <InfoRow label="Güç" value={c.powerKW != null && c.powerKW > 0 ? `${c.powerKW} kW` : null} />
          <InfoRow label="Voltaj" value={c.voltage != null && c.voltage > 0 ? `${c.voltage} V` : null} />
          <InfoRow label="Akım" value={c.currentAmps != null && c.currentAmps > 0 ? `${c.currentAmps} A` : null} />
          <InfoRow label="Son güncelleme" value={formatRelative(c.updatedAt)} />
        </div>
        {c.errorCode || c.info ? (
          <div className="bg-muted/40 rounded-md p-2 text-xs">
            {c.errorCode ? <div><strong>Error:</strong> {c.errorCode}</div> : null}
            {c.info ? <div className="text-muted-foreground">{c.info}</div> : null}
          </div>
        ) : null}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            size="sm"
            disabled={!hooks.isDeviceOnline(device)}
            onClick={() => onAction('start', c.id)}
          >
            <Zap className="mr-1.5 h-3.5 w-3.5" /> Başlat
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!hooks.isDeviceOnline(device)}
            onClick={() => onAction('stop', c.id)}
          >
            <PlugZap className="mr-1.5 h-3.5 w-3.5" /> Durdur
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!hooks.isDeviceOnline(device)}
            onClick={() => onAction('reset', c.id)}
          >
            <RotateCw className="mr-1.5 h-3.5 w-3.5" /> Reset
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function DeviceDetailPage() {
  const { t } = useTranslation();
  const { deviceId } = useParams({ strict: false }) as { deviceId: string };
  const location = useLocation();
  const navigate = useNavigate();
  const q = hooks.useAdminDevice(deviceId);
  const update = hooks.useUpdateDevice();
  const del = hooks.useDeleteDevice();
  const remoteStart = hooks.useRemoteStart();
  const remoteStop = hooks.useRemoteStop();
  const reset = hooks.useResetDevice();

  const editOpen = location.hash === 'edit';
  const localAuthOpen = location.hash === 'local-auth';

  /** Stateful prompt for connector start (idTag). Replaces window.prompt. */
  const [connectorPrompt, setConnectorPrompt] = useState<{
    kind: 'start';
    connectorId: number;
  } | null>(null);
  /** Generic confirm dialog state. Replaces window.confirm. */
  const [confirm, setConfirm] = useState<
    { title: string; description: string; confirmLabel?: string; onConfirm: () => void } | null
  >(null);

  const closeHash = () => navigate({ to: '/devices/$deviceId', params: { deviceId }, hash: '' });
  const openHash = (h: string) =>
    navigate({ to: '/devices/$deviceId', params: { deviceId }, hash: h });

  const onDelete = () => {
    const d = q.data;
    if (!d) return;
    setConfirm({
      title: 'Cihazı sil',
      description: `"${d.displayName ?? d.serialNumber}" cihazını silmek istediğine emin misin? Bu işlem geri alınamaz.`,
      confirmLabel: 'Sil',
      onConfirm: () => {
        del.mutate(d.id, {
          onSuccess: () => {
            toast.success('Cihaz silindi');
            navigate({ to: '/devices' });
          },
          onError: (e) => toast.error(resolveApiError(e, t)),
        });
      },
    });
  };

  const onConnectorAction = async (kind: 'start' | 'stop' | 'reset', connectorId: number) => {
    if (!q.data) return;
    if (kind === 'reset') {
      try {
        await reset.mutateAsync({ deviceId: q.data.id, type: 'Soft' });
        toast.success('Soft Reset gönderildi');
      } catch (err) {
        toast.error(resolveApiError(err, t));
      }
      return;
    }
    if (kind === 'stop') {
      // No transaction ID needed — the backend resolves the active session
      // on this connector (mobile-app parity).
      const deviceId = q.data.id;
      setConfirm({
        title: 'Şarjı durdur',
        description: `Konnektör #${connectorId} üzerindeki aktif şarj oturumu durdurulacak. Devam edilsin mi?`,
        confirmLabel: 'Durdur',
        onConfirm: () => {
          remoteStop.mutate(
            { deviceId, connectorId },
            {
              onSuccess: () => toast.success('Şarj durdurma komutu gönderildi'),
              onError: (err) => toast.error(resolveApiError(err, t)),
            },
          );
        },
      });
      return;
    }
    setConnectorPrompt({ kind, connectorId });
  };

  if (q.isLoading) {
    return (
      <>
        <PageHeader title="Cihaz Detayı" />
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Skeleton className="h-72 lg:col-span-2" />
            <Skeleton className="h-72" />
          </div>
        </div>
      </>
    );
  }

  if (!q.data) {
    return (
      <>
        <PageHeader title="Cihaz bulunamadı" />
        <Button asChild variant="outline">
          <Link to="/devices">
            <ArrowLeft className="mr-2 h-4 w-4" /> Cihazlara dön
          </Link>
        </Button>
      </>
    );
  }

  const d = q.data;
  const title = d.displayName?.trim() || d.serialNumber;

  return (
    <>
      <PageHeader
        title={title}
        description={d.displayName ? d.serialNumber : undefined}
        actions={
          <>
            <Button asChild variant="outline" size="sm">
              <Link to="/devices">
                <ArrowLeft className="mr-2 h-4 w-4" /> Geri
              </Link>
            </Button>
            <Button variant="outline" size="sm" onClick={() => openHash('edit')}>
              <Pencil className="mr-2 h-4 w-4" /> Düzenle
            </Button>
            <Button variant="outline" size="sm" onClick={() => openHash('local-auth')}>
              <KeyRound className="mr-2 h-4 w-4" /> Local Auth
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/devices/$deviceId/control" params={{ deviceId }}>
                <Terminal className="mr-2 h-4 w-4" /> Kontrol
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/devices/$deviceId/console" params={{ deviceId }}>
                <Rss className="mr-2 h-4 w-4" /> Console
              </Link>
            </Button>
            <Button variant="destructive" size="sm" onClick={onDelete}>
              <Trash2 className="mr-2 h-4 w-4" /> Sil
            </Button>
          </>
        }
      />

      {/* Status banner */}
      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-center gap-3 p-4">
          <Radio className="text-muted-foreground h-5 w-5" />
          <span className="text-sm font-medium">Durum:</span>
          <DeviceStatusBadge
            status={
              hooks.isDeviceOnline(d) && d.status === 'offline' ? 'available' : d.status
            }
          />
          <ConnectionBadge device={d} />
          <RegisteredBadge device={d} />
          {d.lastHeartbeatAt ? (
            <span className="text-muted-foreground ml-auto text-xs">
              Son sinyal: {formatRelative(d.lastHeartbeatAt)}
            </span>
          ) : null}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Temel Bilgiler</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
                <InfoRow label="Görünen ad" value={d.displayName} />
                <InfoRow label="Seri no" value={<span className="font-mono">{d.serialNumber}</span>} />
                <InfoRow label="Açıklama" value={d.description} />
                <InfoRow label="Organizasyon" value={d.organizationId ? <span className="font-mono text-xs">{d.organizationId}</span> : null} />
                <InfoRow label="İstasyon" value={d.stationId ? <span className="font-mono text-xs">{d.stationId}</span> : null} />
                <InfoRow label="QR Token" value={d.qrToken ? <span className="font-mono text-xs">{d.qrToken}</span> : null} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Teknik Detaylar</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
                <InfoRow label="Üretici" value={d.vendor} />
                <InfoRow label="Model" value={d.model} />
                <InfoRow label="Firmware" value={d.firmwareVersion ?? d.firmware} />
                <InfoRow label="OCPP" value={d.ocppVersion} />
                <InfoRow label="Public" value={d.isPublic ? 'Evet' : 'Hayır'} />
                <InfoRow label="Aktif" value={d.isActive ? 'Evet' : 'Hayır'} />
                <InfoRow label="Oluşturma" value={d.createdAt ? formatDate(d.createdAt) : null} />
                <InfoRow label="Son güncelleme" value={d.updatedAt ? formatRelative(d.updatedAt) : null} />
                <InfoRow label="Online'dan beri" value={d.onlineSince ? formatRelative(d.onlineSince) : null} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Konnektörler</CardTitle>
            </CardHeader>
            <CardContent>
              {d.connectors.length === 0 ? (
                <p className="text-muted-foreground text-sm">Bu cihaz için konnektör bilgisi yok.</p>
              ) : (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {d.connectors.map((c) => (
                    <ConnectorCard key={c.id} device={d} c={c} onAction={onConnectorAction} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <QRCard device={d} />
        </div>
      </div>

      {/* Edit dialog (full form arrives in Stage 3) */}
      <Dialog open={editOpen} onOpenChange={(o) => !o && closeHash()}>
        {editOpen ? (
          <EditDeviceDialog
            device={d}
            onClose={closeHash}
            onSubmit={async (patch) => {
              await update.mutateAsync({ id: d.id, patch });
              toast.success('Cihaz güncellendi');
              closeHash();
            }}
          />
        ) : null}
      </Dialog>

      <Dialog open={localAuthOpen} onOpenChange={(o) => !o && closeHash()}>
        {localAuthOpen ? <LocalAuthDialog device={d} onClose={closeHash} /> : null}
      </Dialog>

      <Dialog open={connectorPrompt !== null} onOpenChange={(o) => !o && setConnectorPrompt(null)}>
        {connectorPrompt ? (
          <ConnectorPromptDialog
            connectorId={connectorPrompt.connectorId}
            onClose={() => setConnectorPrompt(null)}
            onSubmit={async (idTag) => {
              try {
                await remoteStart.mutateAsync({
                  deviceId: d.id,
                  connectorId: connectorPrompt.connectorId,
                  idTag,
                });
                toast.success('Remote Start gönderildi');
                setConnectorPrompt(null);
              } catch (err) {
                toast.error(resolveApiError(err, t));
              }
            }}
          />
        ) : null}
      </Dialog>

      <Dialog open={confirm !== null} onOpenChange={(o) => !o && setConfirm(null)}>
        {confirm ? (
          <ConfirmDialog
            title={confirm.title}
            description={confirm.description}
            confirmLabel={confirm.confirmLabel}
            onClose={() => setConfirm(null)}
            onConfirm={() => {
              confirm.onConfirm();
              setConfirm(null);
            }}
          />
        ) : null}
      </Dialog>
    </>
  );
}

function LocalAuthDialog({ device, onClose }: { device: Device; onClose: () => void }) {
  const { t } = useTranslation();
  type Row = { idTag: string; status: 'Accepted' | 'Blocked' | 'Expired' | 'Invalid' | 'ConcurrentTx' };
  const getVer = hooks.useGetLocalListVersion();
  const send = hooks.useSendLocalList();
  const [version, setVersion] = useState<number | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [updateType, setUpdateType] = useState<'Full' | 'Differential'>('Differential');

  const offline = device.status === 'offline';

  const fetchVersion = async () => {
    try {
      const res = await getVer.mutateAsync({ deviceId: device.id });
      setVersion(res.listVersion);
      toast.success('Versiyon alındı', `v${res.listVersion}`);
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  };

  const addRow = () => setRows((s) => [...s, { idTag: '', status: 'Accepted' }]);
  const updateRow = (idx: number, patch: Partial<Row>) =>
    setRows((s) => s.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeRow = (idx: number) => setRows((s) => s.filter((_, i) => i !== idx));

  const submit = async () => {
    if (rows.some((r) => !r.idTag.trim())) {
      toast.error(t('errors.device.missingIdTag'));
      return;
    }
    if (version == null) {
      toast.error(t('errors.device.versionRequired'));
      return;
    }
    try {
      await send.mutateAsync({
        deviceId: device.id,
        listVersion: version + 1,
        updateType,
        entries: rows.map((r) => ({ idTag: r.idTag.trim(), status: r.status })),
      });
      toast.success('Liste gönderildi', `v${version + 1}`);
      setVersion(version + 1);
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  };

  const STATUS_OPTIONS = [
    { value: 'Accepted', label: 'Accepted' },
    { value: 'Blocked', label: 'Blocked' },
    { value: 'Expired', label: 'Expired' },
    { value: 'Invalid', label: 'Invalid' },
    { value: 'ConcurrentTx', label: 'ConcurrentTx' },
  ];

  return (
    <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle>Local Authorization List</DialogTitle>
      </DialogHeader>

      <div className="space-y-4">
        <p className="text-muted-foreground text-xs">
          Cihaz, çevrimdışı kaldığında bu listedeki RFID etiketlerini yerel olarak doğrular. Listeyi
          güncellemek için OCPP <code>SendLocalList</code> kullanılır; mevcut versiyonu öğrenmek için{' '}
          <code>GetLocalListVersion</code>.
        </p>

        <Card>
          <CardContent className="flex flex-wrap items-center gap-3 p-3">
            <span className="text-sm">
              Mevcut versiyon: <strong>{version ?? '—'}</strong>
            </span>
            <Button size="sm" variant="outline" disabled={offline || getVer.isPending} onClick={fetchVersion}>
              <RotateCw className="mr-1.5 h-3.5 w-3.5" /> Versiyonu sorgula
            </Button>
            <div className="w-full sm:ml-auto sm:w-48">
              <Field label="Update Type" inline>
                <SelectField<'Full' | 'Differential'>
                  size="sm"
                  value={updateType}
                  onValueChange={setUpdateType}
                  options={[
                    { value: 'Differential', label: 'Differential' },
                    { value: 'Full', label: 'Full' },
                  ]}
                />
              </Field>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Etiketler</Label>
            <Button size="sm" variant="outline" onClick={addRow}>
              + Etiket ekle
            </Button>
          </div>
          {rows.length === 0 ? (
            <p className="text-muted-foreground bg-muted/30 rounded-md p-3 text-center text-xs">
              Henüz etiket yok. "Etiket ekle" ile başla.
            </p>
          ) : (
            <ul className="space-y-2">
              {rows.map((r, i) => (
                <li key={i} className="flex flex-wrap items-center gap-2">
                  <div className="w-full min-w-0 sm:w-auto sm:flex-1">
                    <IdTagPicker
                      value={r.idTag}
                      onValueChange={(v) => updateRow(i, { idTag: v })}
                      excludeValues={rows.filter((_, j) => j !== i).map((row) => row.idTag)}
                      size="sm"
                    />
                  </div>
                  <div className="min-w-0 flex-1 sm:w-44 sm:flex-none">
                    <SelectField<Row['status']>
                      value={r.status}
                      onValueChange={(v) => updateRow(i, { status: v })}
                      options={STATUS_OPTIONS as { value: Row['status']; label: string }[]}
                    />
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => removeRow(i)} aria-label="Sil">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={send.isPending}>
          Kapat
        </Button>
        <Button onClick={submit} disabled={offline || rows.length === 0 || send.isPending || version == null}>
          {send.isPending ? 'Gönderiliyor…' : `Gönder (v${(version ?? 0) + 1})`}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function QRCard({ device }: { device: Device }) {
  const ref = useRef<HTMLDivElement>(null);
  const payload = device.qrToken?.trim() || device.serialNumber;

  const download = () => {
    const canvas = ref.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `qr-device-${device.serialNumber}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cihaz QR Kodu</CardTitle>
      </CardHeader>
      <CardContent>
        <div ref={ref} className="flex flex-col items-center gap-3">
          <div className="rounded-lg bg-white p-4">
            <QRCodeCanvas value={payload} size={224} level="M" includeMargin={false} />
          </div>
          <span className="text-muted-foreground font-mono text-xs">{payload}</span>
          <Button variant="outline" size="sm" onClick={download}>
            <Download className="mr-2 h-4 w-4" /> PNG indir
          </Button>
          <p className="text-muted-foreground px-2 text-center text-xs">
            Mobil uygulamada bu kodu okutarak cihaza kayıt olabilir veya şarj başlatabilirsin.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Full device edit form. Backed by the widened admin PATCH endpoint that
 * accepts displayName/description/vendor/model/firmwareVersion/ocppVersion +
 * organizationId/stationId UUID references + isPublic/isActive/registered
 * booleans.
 */
function EditDeviceDialog({
  device,
  onClose,
  onSubmit,
}: {
  device: Device;
  onClose: () => void;
  onSubmit: (patch: Record<string, unknown>) => Promise<void>;
}) {
  const { t } = useTranslation();
  const groups = hooks.useAdminGroups({ page: 1, pageSize: 200 });
  const stations = hooks.useAdminStations({ page: 1, pageSize: 500 });
  const [form, setForm] = useState({
    displayName: device.displayName ?? '',
    description: device.description ?? '',
    vendor: device.vendor ?? '',
    model: device.model ?? '',
    firmwareVersion: device.firmwareVersion ?? device.firmware ?? '',
    ocppVersion: device.ocppVersion ?? '1.6',
    organizationId: device.organizationId ?? '',
    stationId: device.stationId ?? '',
    isPublic: Boolean(device.isPublic),
    isActive: device.isActive !== false,
    registered: Boolean(device.registered),
  });
  const [busy, setBusy] = useState(false);

  // Reset local form when device changes (navigating between devices).
  useEffect(() => {
    setForm({
      displayName: device.displayName ?? '',
      description: device.description ?? '',
      vendor: device.vendor ?? '',
      model: device.model ?? '',
      firmwareVersion: device.firmwareVersion ?? device.firmware ?? '',
      ocppVersion: device.ocppVersion ?? '1.6',
      organizationId: device.organizationId ?? '',
      stationId: device.stationId ?? '',
      isPublic: Boolean(device.isPublic),
      isActive: device.isActive !== false,
      registered: Boolean(device.registered),
    });
  }, [device]);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((s) => ({ ...s, [k]: v }));

  const organizationOptions = [
    { value: SELECT_NONE, label: t('station.link.noneOrganization') },
    ...(groups.data?.data ?? []).map((g) => ({ value: g.id, label: g.name })),
  ];

  const stationOptions = [
    { value: SELECT_NONE, label: t('station.link.noneStation') },
    ...(stations.data?.data ?? [])
      .filter((s) => !form.organizationId || s.organizationId === form.organizationId)
      .map((s) => ({ value: s.id, label: s.name })),
  ];

  const submit = async () => {
    setBusy(true);
    try {
      await onSubmit({
        displayName: form.displayName,
        description: form.description,
        vendor: form.vendor,
        model: form.model,
        firmwareVersion: form.firmwareVersion,
        ocppVersion: form.ocppVersion,
        organizationId: form.organizationId,
        stationId: form.stationId,
        isPublic: form.isPublic,
        isActive: form.isActive,
        registered: form.registered,
      });
    } catch (err) {
      toast.error(resolveApiError(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle>Cihazı düzenle</DialogTitle>
      </DialogHeader>
      <div className="space-y-5">
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">Temel</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Seri No">
              <TextField value={device.serialNumber} disabled className="font-mono" />
            </Field>
            <Field label="Görünen ad" hint="Listelerde gösterilen ad">
              <TextField
                value={form.displayName}
                onChange={(e) => set('displayName', e.target.value)}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Açıklama">
                <TextField
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                />
              </Field>
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold">Teknik</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Üretici">
              <TextField value={form.vendor} onChange={(e) => set('vendor', e.target.value)} />
            </Field>
            <Field label="Model">
              <TextField value={form.model} onChange={(e) => set('model', e.target.value)} />
            </Field>
            <Field label="Firmware">
              <TextField
                value={form.firmwareVersion}
                onChange={(e) => set('firmwareVersion', e.target.value)}
              />
            </Field>
            <Field label="OCPP versiyonu">
              <SelectField
                value={form.ocppVersion}
                onValueChange={(v) => set('ocppVersion', v)}
                options={[
                  { value: '1.6', label: 'OCPP 1.6' },
                  { value: '2.0.1', label: 'OCPP 2.0.1' },
                ]}
              />
            </Field>
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold">{t('device.edit.relations')}</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t('station.detail.organization')} hint={t('station.create.organizationHint')}>
              <SelectField
                value={form.organizationId ? form.organizationId : SELECT_NONE}
                onValueChange={(v) => {
                  const org = fromSelectValue(v);
                  setForm((s) => ({
                    ...s,
                    organizationId: org,
                    stationId:
                      org && s.stationId
                        ? (stations.data?.data?.find((st) => st.id === s.stationId)?.organizationId ===
                          org
                            ? s.stationId
                            : '')
                        : s.stationId,
                  }));
                }}
                options={organizationOptions}
                disabled={groups.isLoading}
              />
            </Field>
            <Field label={t('nav.stations')} hint={t('device.edit.stationHint')}>
              <SelectField
                value={form.stationId ? form.stationId : SELECT_NONE}
                onValueChange={(v) => set('stationId', fromSelectValue(v))}
                options={stationOptions}
                disabled={stations.isLoading}
                placeholder={t('station.link.selectStation')}
              />
            </Field>
          </div>
        </section>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold">Görünürlük & Durum</h3>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <CheckboxField
              variant="card"
              checked={form.isPublic}
              onCheckedChange={(c) => set('isPublic', c)}
              label="Herkese açık"
              description="Mobil listede görünür. Org cihazları, Herkese açık kapalı olsa bile org üyelerine listelenir. Mobilde ayrı cihaz ekleme yok; şarj için geçerli idTag yeterli (org cihazlarında org üyeliği listeleme için gerekli)."
            />
            <CheckboxField
              variant="card"
              checked={form.isActive}
              onCheckedChange={(c) => set('isActive', c)}
              label="Aktif"
              description="Kapatılırsa hiçbir komut işlenmez"
            />
            <CheckboxField
              variant="card"
              checked={form.registered}
              onCheckedChange={(c) => set('registered', c)}
              label="Kayıtlı"
              description="Boot Accept yanıtını belirler"
            />
          </div>
        </section>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={busy}>
          İptal
        </Button>
        <Button onClick={submit} disabled={busy}>
          {busy ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

/**
 * ConnectorPromptDialog asks for the idTag needed to start a charging
 * session on a specific connector. Stop no longer prompts — the backend
 * resolves the active transaction automatically.
 */
function ConnectorPromptDialog({
  connectorId,
  onClose,
  onSubmit,
}: {
  connectorId: number;
  onClose: () => void;
  onSubmit: (idTag: string) => Promise<void>;
}) {
  const [tag, setTag] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await onSubmit(tag);
    } finally {
      setBusy(false);
    }
  };
  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Şarjı başlat · Konnektör #{connectorId}</DialogTitle>
      </DialogHeader>
      <Field label="idTag" required hint="RFID etiketi veya yetkilendirilmiş kullanıcı kimliği.">
        <IdTagPicker value={tag} onValueChange={setTag} />
      </Field>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={busy}>
          İptal
        </Button>
        <Button onClick={submit} disabled={busy || !tag.trim()}>
          {busy ? 'Gönderiliyor…' : 'Başlat'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

/** Generic confirmation dialog. Replaces window.confirm. */
function ConfirmDialog({
  title,
  description,
  confirmLabel = 'Onayla',
  onClose,
  onConfirm,
}: {
  title: string;
  description: string;
  confirmLabel?: string;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
      </DialogHeader>
      <p className="text-muted-foreground text-sm">{description}</p>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          İptal
        </Button>
        <Button variant="destructive" onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
